import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  MatLegacySelect as MatSelect,
  MatLegacySelectChange as MatSelectChange,
} from '@angular/material/legacy-select';
import { MatDrawer } from '@angular/material/sidenav';
import { ActivatedRoute, Data, NavigationStart, Router } from '@angular/router';
import { Store, Actions, ofActionCompleted, ofAction } from '@ngxs/store';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { FeatureFlag, NewPermission } from 'app/core/enums';
import { FieldDef, SavedView, User, QueryConfig } from 'app/core/models';
import { AccountService } from 'app/core/services/account.service';
import { AggregatedTasksService } from 'app/modules/aggregated-tasks/aggregated-tasks.service';
import { ContactPagination } from 'app/modules/contacts/contacts.types';
import { SelectableFieldDef } from 'app/shared/components/column-selector/column-selector.types';
import { ColumnConfig } from 'app/shared/modules/legacy-tasks/list/list.types';
import { Task, TaskResult, TaskStatus } from 'app/shared/modules/legacy-tasks/tasks.types';
import UrlUtils from 'app/shared/Utils/url.utils';
import { AccountState } from 'app/state/account/state';
import { FilterListItemChangeArgs, FilterListItemModel, FilterListItemValueModel } from 'tsui/lib/cross-cutting/models';
import { TsOverlayComponent, TsPaginatorChangeEvent, TsPaginatorComponent, TsSnackbarService } from 'tsui';
import { BehaviorSubject, filter, noop } from 'rxjs';
import { LookupService } from '@services/lookup.service';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { FieldDefsState } from '@state/fields/state';
import { RelatedToType } from 'app/shared/enums';
import { OriginalStates } from 'app/shared/models';
import { AggregatedTaskDetailsComponent } from '../aggregated-tasks-details/aggregated-tasks-details.component';
import { TaskExportDialogResult } from '@shared/modules/legacy-tasks/task-export-dialog/task-export-dialog.component';
import { TaskExportUtilsService } from '@tasks/services/task-export-utils.service';
import { TaskActions } from '@tasks/state/actions';
import { UserState } from '@state/user/state';

const LOCAL_STORAGE_KEYS = {
  savedView: 'aggregatedTasksSavedViewId',
};

@Component({
  selector: 'aggregated-task-list',
  templateUrl: './aggregated-tasks-list.component.html',
  styleUrls: ['./aggregated-tasks-list.component.scss'],
})
export class AggregatedTaskListComponent extends OnDestroyMixin implements OnInit, OnDestroy {
  private selectedFields: FieldDef[];
  private queryFilters: Partial<QueryConfig.QueryFilters>;
  private changeTriggers: Set<string> = new Set();
  private originalStates: OriginalStates;
  private fieldDefs: FieldDef[] = [];
  private columnsConfig: ColumnConfig = {
    prefix: ['drag_control', 'expand_control', 'status_control'],
    dynamic: [],
    suffix: ['details_control'],
  };
  private defaultAggregatedTaskTableFields: Partial<SelectableFieldDef>[] = [
    { id: -1, name: 'title', label: 'Title' },
    { id: -2, name: 'project_id', label: 'Deal' },
    { id: -3, name: 'property_id', label: 'Property' },
    { id: -4, name: 'task_list_name', label: 'Task List' },
    { id: -5, name: 'start_date', label: 'Start Date' },
    { id: -6, name: 'due_date', label: 'Due Date' },
    { id: -7, name: 'assigned_user_id', label: 'Assigned User' },
  ];

  public tasks: Task[];
  public tasksCount: any;
  public workflowStates: TaskStatus[];
  public routeData: Data;
  public pagination: ContactPagination;
  public isLoading = false;
  public users: User[];
  public savedView: SavedView;
  public taskFieldDefs: FieldDef[];
  public selectedOrder: string[];
  public groupBy: string = '';
  public typeFields = new BehaviorSubject<FilterListItemModel[]>([]);
  public filterValueList = new BehaviorSubject<FilterListItemValueModel[]>([]);
  NewPermission = NewPermission;
  canEdit = true;

  @ViewChild(TsPaginatorComponent) private paginator: TsPaginatorComponent;
  @ViewChild('matDrawer', { static: false }) matDrawer: MatDrawer;
  @ViewChild('groupSelect') groupSelect: MatSelect;
  @ViewChild('filtersOverlayBox') private _filtersOverlayBox: TsOverlayComponent;

  get selectedFieldsTable(): FieldDef[] {
    return this.selectedOrder.map((fieldName: string) =>
      this.routeData.taskFieldDefs
        .filter((taskFieldDef: FieldDef) => !!taskFieldDef)
        .find((taskFieldDef: FieldDef) => taskFieldDef.name === fieldName),
    );
  }

  get filterCounter(): number {
    return this.filterValueList?.value?.length || 0;
  }

  constructor(
    private readonly aggregatedTasksService: AggregatedTasksService,
    private readonly accountService: AccountService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly store: Store,
    private readonly actions: Actions,
    private readonly cdRef: ChangeDetectorRef,
    public readonly lookupService: LookupService,
    private readonly snackBar: TsSnackbarService,
    private readonly taskExportUtilsService: TaskExportUtilsService,
  ) {
    super();

    this.aggregatedTasksService.accountId = this.accountService.getCurrentAccount().id;

    this.tasksCount = {
      completed: 0,
      incomplete: 0,
      total: 0,
    };

    this.pagination = {
      length: 1,
      size: 20,
      page: 0,
      lastPage: 1,
      startIndex: 1,
      endIndex: 1 - 1,
    };
  }

  ngOnInit(): void {
    this.isLoading = true;

    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      this.canEdit = this.store.selectSnapshot(UserState.hasPermission(NewPermission.AGGREGATED_TASK_LISTS_EDIT));
    }

    this.activatedRoute.data.pipe(untilComponentDestroyed(this)).subscribe((data: Data) => {
      this.routeData = data;
      this.savedView = data?.savedView;

      if (this.savedView) {
        this.queryFilters = this.getQueryFiltersState();
        this.filterValueList.next(this.fjsonStringToFilterValue(this.queryFilters?.fjson ?? '[]'));
        this.groupBy = this.getGroupByState();
        this.selectedFields = this.getFieldDefsState();
        UrlUtils.saveToLocalStorage(LOCAL_STORAGE_KEYS.savedView, data.savedView.id);

        if (!this.savedView?.definition || !this.queryFilters?.filterOptions) {
          if (!this.savedView?.definition) {
            this.savedView.definition = JSON.stringify({ table: { fields: this.getDisplayColumnsState() } });
          }

          if (!this.queryFilters?.filterOptions) {
            this.savedView.saved_filter.filters = JSON.stringify({
              ...this.getQueryFiltersState(),
              filterOptions: { include_keydates: false },
            });
          }
          this.accountService.updateSavedView(this.savedView).pipe(untilComponentDestroyed(this)).subscribe(noop);
        }

        if (this.queryFilters?.fjson?.length && this.queryFilters?.fjson !== '[]') {
          this.saveFilter(this.queryFilters);
        } else {
          this.getTasks();
        }
      }
    });
    this.actions.pipe(untilComponentDestroyed(this), ofActionCompleted(TaskActions.DeleteTask)).subscribe(() => {
      this.getTasks();
    });

    this.actions.pipe(untilComponentDestroyed(this), ofActionCompleted(TaskActions.UpdateTask)).subscribe(() => {
      this.getTasks();
    });

    this.getUsers();
    this.getAggregatedTasks();
    this.getAggregatedTasksPagination();
    this.getAggregatedTasksStatuses();
    this.setFilteredFields();
    this.closeSnackBarOnLeave();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.snackBar.dismiss();
  }

  resetPaginationToOriginal() {
    this.pagination = {
      ...this.pagination,
      page: 0,
      lastPage: 1,
      startIndex: 1,
      endIndex: 1 - 1,
    };
  }

  public getGroupByLabel(): string {
    return this.routeData.taskFieldDefs.find((taskFieldDef: FieldDef) => taskFieldDef.name === this.groupBy)?.label;
  }

  public onChangeGroupBy(event: MatSelectChange): void {
    this.openTrackStateSnackbar('groupBy');
    this.groupBy = event.value;
    this.resetPaginationToOriginal();
    this.getTasks();
  }

  public onSaveColumns(selectedFieldDefs: SelectableFieldDef[]): void {
    this.openTrackStateSnackbar('columns');

    this.selectedFields = selectedFieldDefs.length
      ? selectedFieldDefs
      : (this.defaultAggregatedTaskTableFields as SelectableFieldDef[]);
    this.selectedOrder = selectedFieldDefs.map(({ name }) => name) || [];

    this.refreshSelectedFields();
  }

  public updateTask(task: Task): void {
    this.aggregatedTasksService.updateTask(task).pipe(untilComponentDestroyed(this)).subscribe();
  }

  public openTaskDetails(task: Task, fragment?: string): void {
    const route = this.activatedRoute;
    this.router.navigate(['./', task.task_list_id, task.id], {
      relativeTo: route,
      ...(fragment ? { fragment } : {}),
    });
  }

  public onNavigateToComments(task: Task): void {
    this.openTaskDetails(task, AggregatedTaskDetailsComponent.commentViewFragment);
  }

  public onPageChange(event: TsPaginatorChangeEvent): void {
    this.queryFilters = { ...this.queryFilters, size: event.pageSize };
    this.savedView.saved_filter.filters = JSON.stringify(this.queryFilters);
    this.accountService.updateFilter(this.savedView.saved_filter).pipe(untilComponentDestroyed(this)).subscribe(noop);
    this.pagination = {
      ...this.pagination,
      size: event.pageSize,
      page: event.activePage - 1,
    };
    this.saveFilter(this.queryFilters);
  }

  public openGroupPanel(): void {
    this.groupSelect.open();
  }

  public onCloseByBackdropClick(): void {
    this.filterValueList.next(this.fjsonStringToFilterValue(this.queryFilters?.fjson ?? '[]'));
  }

  public onAddFilterItem(): void {
    this.filterValueList.next([
      ...this.filterValueList.value,
      {
        fieldName: null,
        operator: null,
        value: null,
      },
    ]);
  }

  public onFilterDefChanged(filterDefChangeArgs: FilterListItemChangeArgs): void {
    const index = filterDefChangeArgs.index;
    delete filterDefChangeArgs.index;
    this.filterValueList.value[index] = filterDefChangeArgs;
    this.filterValueList.next([...this.filterValueList.value]);
  }

  public onFilterRemoved(index: number): void {
    const newState = this.filterValueList.value?.filter((_, i) => i !== index);
    if (!newState.length) {
      this.onFilterCleared();
    }
    this.filterValueList.next(newState);
  }

  public onFilterCleared(): void {
    this.openTrackStateSnackbar('filters');
    this.filterValueList.next([]);
    this.queryFilters = { ...this.queryFilters, fjson: `[]` };
    this.saveFilter(this.queryFilters);
  }

  public onFilterApplied(): void {
    this.openTrackStateSnackbar('filters');
    this.queryFilters = {
      ...this.queryFilters,
      fjson: `[${this.filterValueList.value
        .map((x) => `["${x.fieldName}", "${x.operator}", ${JSON.stringify(x.value)}]`)
        .join(', ')}]`,
    };

    this.resetPaginationToOriginal();

    this.saveFilter(this.queryFilters);

    this._filtersOverlayBox.closeOverlay();
  }

  public openTaskExportDialog() {
    this.taskExportUtilsService
      .openTaskExportDialog()
      .pipe(untilComponentDestroyed(this))
      .subscribe((result) => {
        this.downloadAggregatedTasks(result);
      });
  }

  private downloadAggregatedTasks(result: TaskExportDialogResult) {
    const filters = {
      fjson: this.extractFjsonFilters(this.queryFilters),
      sort: [],
      q: this.queryFilters?.q ?? '',
      include_keydates: !!this.queryFilters?.filterOptions?.include_keydates,
      page: this.pagination.page + 1,
      per_page: this.queryFilters?.size || this.paginator?.pageSize || this.pagination?.size || 20,
      group: this.groupBy,
    };

    if (this.selectedOrder?.indexOf?.('property_id') >= 0) {
      const propertyIndex = this.selectedOrder.indexOf('property_id');
      this.selectedOrder[propertyIndex] = 'property_name';
    }

    if (this.selectedOrder?.indexOf?.('project_id') >= 0) {
      const projectIndex = this.selectedOrder.indexOf('project_id');
      this.selectedOrder[projectIndex] = 'project_title';
    }

    this.aggregatedTasksService
      .downloadAggregatedTasks(result, this.selectedOrder, filters, this.savedView.name)
      .pipe(untilComponentDestroyed(this))
      .subscribe();
  }

  private getUsers(): void {
    this.accountService
      .getUsers()
      .pipe(untilComponentDestroyed(this))
      .subscribe((users: User[]) => {
        this.users = users;
      });
  }

  private getAggregatedTasks(): void {
    this.aggregatedTasksService.tasks$.pipe(untilComponentDestroyed(this)).subscribe((tasks: Task[]) => {
      if (tasks) {
        // sc-30910: investigate if it's possible to remove the assignment bellow
        // the call to loadTasks() already assigns to this.task
        this.tasks = tasks;
        this.loadTasks();
        this.isLoading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  private getAggregatedTasksPagination(): void {
    this.aggregatedTasksService.pagination$
      .pipe(untilComponentDestroyed(this))
      .subscribe((pagination: ContactPagination) => {
        if (pagination) {
          this.pagination = pagination;
          this.tasksCount = pagination.length;
          this.cdRef.markForCheck();
        }
      });
  }

  private getAggregatedTasksStatuses(): void {
    this.aggregatedTasksService
      .getTaskStatuses()
      .pipe(untilComponentDestroyed(this))
      .subscribe((taskStatuses) => {
        this.workflowStates = taskStatuses;
      });
  }

  private setFilteredFields(): void {
    this.fieldDefs = this.store.selectSnapshot(
      ObjectsManagerState.getFieldsByObjectName(RelatedToType.Task, {
        allowedFieldsOnly: true,
        addNestedFields: false,
      }),
    );

    this.setFilterNestedFields();
  }

  private getTasks(): void {
    this.saveFilter(this.queryFilters);
  }

  private loadTasks(): void {
    this.tasks = this.tasks.filter(
      (task) => (task.task_type === 'task' || task.task_type === 'key_date') && task.task_list_id !== null,
    );
  }

  private saveFilter(filters: Partial<QueryConfig.QueryFilters>): void {
    this.isLoading = true;

    const tempFilter = this.extractFjsonFilters(filters);

    this.aggregatedTasksService
      .search({
        fjson: tempFilter,
        sort: [],
        q: this.queryFilters?.q ?? '',
        include_keydates: !!this.queryFilters?.filterOptions?.include_keydates,
        page: this.pagination.page + 1,
        per_page: this.queryFilters?.size || this.paginator?.pageSize || this.pagination?.size || 20,
        group: this.groupBy,
      })
      .pipe(untilComponentDestroyed(this))
      .subscribe((taskResult: TaskResult) => {
        this.tasks = taskResult.results;
        this.refreshSelectedFields();
        this.pagination = {
          ...this.pagination,
          page: this.pagination.page,
          size: this.queryFilters?.size || this.paginator?.pageSize || this.pagination?.size || 20,
          length: taskResult?.count ?? 0,
        };
        this.isLoading = false;
        this.cdRef.markForCheck();
      });
  }

  private extractFjsonFilters(filters: Partial<QueryConfig.QueryFilters>): string {
    const tempFilter = filters?.fjson ? JSON.parse(filters.fjson) : [];

    // Check if the filter already contains an entry with ['task_type', 'in', ['task']]
    const alreadyExists = tempFilter.some(
      (item: string[]) =>
        Array.isArray(item) &&
        item[0] === 'task_type' &&
        item[1] === 'in' &&
        JSON.stringify(item[2]) === JSON.stringify(['task']),
    );

    // Only push the new array if it doesn't already exist.
    // This is needed to prevent duplicate entries in the filter array and to make sure service
    // only returns tasks and not key dates or sections.
    if (!alreadyExists) {
      tempFilter.push(['task_type', 'in', ['task']]);
    }

    return JSON.stringify(tempFilter);
  }

  private refreshSelectedFields(): void {
    this.columnsConfig.dynamic = this.selectedFields;

    this.taskFieldDefs = [
      ...this.routeData.taskFieldDefs.map((taskFieldDef: FieldDef) => {
        const selected = !!this.selectedFields.find(
          (selectedField: SelectableFieldDef) => selectedField.name === taskFieldDef.name,
        );

        return {
          ...taskFieldDef,
          selected,
        };
      }),
    ];

    this.selectedOrder = this.selectedFields.map(({ name }) => name) || [];
  }

  private setFilterNestedFields(): void {
    const allfields = this.fieldDefs.map((x) => ({
      ...x,
      meta: { ...x.meta, group: { key: 'Task', label: 'Task' } },
    }));

    this.typeFields.next(
      allfields.map(({ label, name, type, meta }) => {
        const fieldOptions = this.formatFieldOptions(meta);

        return {
          label,
          name,
          meta: {
            filtering: { operators: meta?.filtering?.operators || [], functions: meta?.filtering?.functions || [] },
            options: fieldOptions,
            multi_select: meta?.multi_select || false,
            record_type: meta?.record_type || null,
            group: meta?.group,
            scale: meta?.scale,
          },
          type,
        } as FilterListItemModel;
      }),
    );
  }

  private fjsonStringToFilterValue(fjson: string): FilterListItemValueModel[] {
    return JSON.parse(fjson ?? '[]').map(([fieldName, operator, value]) => ({
      fieldName,
      operator,
      value,
    })) as FilterListItemValueModel[];
  }

  private formatFieldOptions(meta: any): string[] | { label: string; value: string }[] {
    const dropdownOptions = this.store.selectSnapshot(FieldDefsState.selectDropdownOptionsBy(meta?.option_name));

    if (dropdownOptions) {
      return dropdownOptions.map((option) => ({ label: option.name, value: option.id }));
    }

    return meta?.options || [];
  }

  private getDisplayColumnsState(): string[] {
    return this.savedView?.definition
      ? JSON.parse(this.savedView.definition)?.table?.fields
      : this.defaultAggregatedTaskTableFields.map((field) => field.name);
  }

  private setDisplayColumnsState(value: string[]): void {
    this.originalStates.displayColumns = value;
  }

  private getFieldDefsState(): FieldDef[] {
    const fields = this.getDisplayColumnsState();
    return (
      fields?.map((fieldName: string) =>
        this.routeData.taskFieldDefs
          .filter((taskFieldDef: FieldDef) => !!taskFieldDef)
          .find((taskFieldDef: FieldDef) => taskFieldDef.name === fieldName),
      ) || this.defaultAggregatedTaskTableFields
    );
  }

  private setFieldDefsState(value: FieldDef[]): void {
    this.originalStates.fieldDefs = value;
  }

  private getFilterValueListState(): FilterListItemValueModel[] {
    return this.fjsonStringToFilterValue(this.queryFilters?.fjson ?? '[]');
  }

  private setFilterValueListState(value: FilterListItemValueModel[]): void {
    this.originalStates.filterValueList = value;
  }

  private getQueryFiltersState(): Partial<QueryConfig.QueryFilters> {
    return JSON.parse(this.savedView.saved_filter?.filters || '{}');
  }

  private setQueryFiltersState(value: Partial<QueryConfig.QueryFilters>): void {
    this.originalStates.queryFilters = value;
  }

  private getGroupByState(): string {
    return JSON.parse(this.savedView.definition)?.table?.groupBy || '';
  }

  private setGroupByState(value: string): void {
    this.originalStates.groupBy = value;
  }

  private captureOriginalStates(): void {
    this.originalStates = {
      displayColumns: this.getDisplayColumnsState(),
      fieldDefs: this.getFieldDefsState(),
      queryFilters: { ...this.getQueryFiltersState() },
      filterValueList: this.getFilterValueListState(),
      groupBy: this.getGroupByState(),
    };
  }

  private openTrackStateSnackbar(changeType: 'filters' | 'columns' | 'groupBy'): void {
    this.changeTriggers.add(changeType);
    this.captureOriginalStates();
    this.snackBar.openApplyChanges(this.resetToOriginalStates.bind(this), this.saveAllChanges.bind(this));
  }

  private clearChangeTriggers(): void {
    this.changeTriggers.clear();
  }

  private resetToOriginalStates(): void {
    if (this.changeTriggers.has('columns')) {
      this.selectedOrder = this.getDisplayColumnsState();
      this.selectedFields = this.getFieldDefsState();
      this.refreshSelectedFields();
    }

    if (this.changeTriggers.has('filters')) {
      this.queryFilters = this.getQueryFiltersState();
      this.filterValueList.next(this.getFilterValueListState());
      this.onCloseByBackdropClick();
      this.saveFilter(this.queryFilters);
    }

    if (this.changeTriggers.has('groupBy')) {
      this.groupBy = this.getGroupByState();
    }

    this.clearChangeTriggers();
    this.cdRef.markForCheck();
  }

  private saveAllChanges(): void {
    if (this.changeTriggers.has('columns') || this.changeTriggers.has('groupBy')) {
      this.savedView.definition = JSON.stringify({
        table: { ...(this.savedView.definition?.table ?? {}), fields: this.selectedOrder, groupBy: this.groupBy },
      });

      this.setGroupByState(this.groupBy);
      this.setDisplayColumnsState(this.selectedOrder);
      this.setFieldDefsState(this.selectedFieldsTable);
      this.refreshSelectedFields();
    }

    if (this.changeTriggers.has('filters')) {
      this.setQueryFiltersState(this.queryFilters);
      this.setFilterValueListState(this.filterValueList.value);
      this.savedView.saved_filter.filters = JSON.stringify(this.queryFilters);
    }

    this.clearChangeTriggers();
  }

  private closeSnackBarOnLeave(): void {
    this.router.events
      .pipe(
        untilComponentDestroyed(this),
        filter((route) => route instanceof NavigationStart),
      )
      .subscribe(() => this.snackBar.dismiss());
  }
}
